import { useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  Building2,
  CalendarDays,
  CreditCard,
  LifeBuoy,
  LogOut,
  Moon,
  Plus,
  Settings2,
  Sun,
  User,
} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useCurrentCompany } from '@/app/providers/company-provider';
import { useDialog } from '@/app/providers/modal-provider/modal-provider.tsx';
import { useTheme } from '@/app/providers/theme-provider';
import { Button } from '@/components/animate-ui/components/buttons/button.tsx';
import {
  type Resolved,
  ThemeToggler,
} from '@/components/animate-ui/primitives/effects/theme-toggler.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useHandleLogout } from '@/entities/auth';
import { isBillingHttpConfigured, useBillingOverviewQuery } from '@/entities/billing';
import { useAuthMeQuery, useCompaniesQuery } from '@/entities/company';
import {
  useGetActiveConversations,
  useGetActiveMeetRoomsWithoutConversation,
} from '@/entities/conversation';
import { supportQueryKeys, useSupportSummaryQuery } from '@/entities/support';
import { CreateCompanyForm } from '@/features/create-company';
import { useNotificationsWS } from '@/pages/servers-sessions-page/ui/ws-status/hooks';
import { env, isSelfHostedDeployment, routes } from '@/shared/lib';

import styles from './app-sidebar.module.scss';

const canCreateCompanyFromBilling = (
  overview: ReturnType<typeof useBillingOverviewQuery>['data'],
) => {
  if (!overview) {
    return true;
  }

  const remainingOwnedCompanies = overview.remaining?.owned_companies;
  if (Number.isFinite(remainingOwnedCompanies)) {
    return remainingOwnedCompanies > 0;
  }

  const ownedCompaniesLimit = overview.limits?.owned_companies;
  const ownedCompaniesUsage = overview.usage?.owned_companies;

  if (!Number.isFinite(ownedCompaniesLimit) || !Number.isFinite(ownedCompaniesUsage)) {
    return true;
  }

  return ownedCompaniesUsage < ownedCompaniesLimit;
};

export const AppSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { openDialog } = useDialog();
  const { theme, setTheme } = useTheme();
  const { data: companiesData, isPending: companiesLoading } = useCompaniesQuery();
  const { data: authMe } = useAuthMeQuery();
  const { currentCompanyId, setCurrentCompanyId, isUpdating } = useCurrentCompany();
  const billingConfigured = isBillingHttpConfigured();
  const showLogoutButton = env.VITE_SHOW_LOGOUT_BUTTON === 'true';
  const showSupportCenter = env.VITE_SHOW_SUPPORT_CENTER === 'true';
  const billingOverview = useBillingOverviewQuery({ enabled: billingConfigured });

  const { data: activeConversationsData } = useGetActiveConversations(currentCompanyId);
  const { data: activeRoomsData } = useGetActiveMeetRoomsWithoutConversation(currentCompanyId);
  const { data: supportSummaryData } = useSupportSummaryQuery({ enabled: showSupportCenter });
  const { handleLogout } = useHandleLogout();

  const companies = companiesData?.items ?? [];
  const currentCompany = companies.find((c) => c.id === currentCompanyId);
  const isOwnerOrAdmin = currentCompany?.role === 'owner' || currentCompany?.role === 'admin';
  const isMeetings = location.pathname === routes.MAIN;
  const isProfile = location.pathname === routes.PROFILE;
  const isTariffs = location.pathname === routes.TARIFFS;
  const isSupport = location.pathname === routes.SUPPORT;
  const isCompanyManagement = location.pathname.includes(routes.COMPANY_MANAGEMENT);
  const isActiveConversations = location.pathname === routes.ACTIVE_CONVERSATIONS;
  const activeConversationsCount = activeConversationsData?.items?.length ?? 0;
  const activeRoomsCount = activeRoomsData?.items?.length ?? 0;
  const activeTotalCount = activeConversationsCount + activeRoomsCount;
  const supportUnreadCount = supportSummaryData?.unread_count ?? 0;
  const canCreateCompany =
    !billingConfigured ||
    billingOverview.isPending ||
    billingOverview.isError ||
    canCreateCompanyFromBilling(billingOverview.data);

  useNotificationsWS({
    subscription: showSupportCenter
      ? {
          resource_type: 'support_summary',
          resource_id: 'me',
        }
      : null,
    onMessage: (message) => {
      if (message.type !== 'support.summary.updated') {
        return;
      }
      const unreadCount = message.payload?.unread_count;
      if (typeof unreadCount === 'number') {
        queryClient.setQueryData(supportQueryKeys.summary(), { unread_count: unreadCount });
        return;
      }
      queryClient.invalidateQueries({ queryKey: supportQueryKeys.summary() });
    },
  });

  const handleCompanyChange = async (value: string) => {
    const nextCompanyId = value === 'none' ? null : value;

    if (nextCompanyId === currentCompanyId) {
      return;
    }

    await setCurrentCompanyId(nextCompanyId);
    navigate(routes.MAIN, { replace: true });
  };

  return (
    <aside
      className={`${styles.sidebar} flex flex-col rounded-2xl border border-border/40 bg-muted/20 backdrop-blur-sm shadow-sm`}
    >
      <div className="p-4 space-y-4">
        <div className="space-y-1.5">
          <label className="text-[11px] uppercase tracking-[0.1em] font-semibold text-muted-foreground/60 block px-1">
            Компания
          </label>
          <Select
            value={currentCompanyId ?? 'none'}
            onValueChange={(value) => void handleCompanyChange(value)}
            disabled={companiesLoading || isUpdating}
          >
            <SelectTrigger className="w-full bg-background/40 hover:bg-background/60 border-border/40">
              <div className="flex items-center gap-2">
                <Building2 className="size-4 text-muted-foreground/60" />
                <SelectValue placeholder="— Не выбрана —" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— Не выбрана —</SelectItem>
              {companies.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {canCreateCompany && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full cursor-pointer rounded-xl border border-dashed border-border/60 hover:border-primary/40 hover:bg-primary/5 active:scale-[0.98] transition-all justify-start gap-2 text-muted-foreground hover:text-primary"
            onClick={() => openDialog(() => <CreateCompanyForm />)}
          >
            <Plus className="size-4" />
            <span className="text-xs font-medium">Создать компанию</span>
          </Button>
        )}
      </div>

      <div className="px-4 py-2">
        <div className="h-px bg-border/20" />
      </div>

      <nav className="flex-1 px-3 py-2 space-y-1">
        <Link
          to={routes.MAIN}
          className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 active:scale-[0.98] ${
            isMeetings
              ? 'bg-primary/10 text-primary shadow-[inset_0_0_0_1px_rgba(var(--primary),0.1)]'
              : 'text-muted-foreground/80 hover:bg-accent/40 hover:text-foreground'
          }`}
        >
          <div
            className={`p-1.5 rounded-lg transition-colors ${isMeetings ? 'bg-primary/20' : 'bg-transparent'}`}
          >
            <CalendarDays className="size-4 shrink-0" />
          </div>
          Встречи
        </Link>

        <Link
          to={routes.ACTIVE_CONVERSATIONS}
          className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 active:scale-[0.98] ${
            isActiveConversations
              ? 'bg-primary/10 text-primary shadow-[inset_0_0_0_1px_rgba(var(--primary),0.1)]'
              : 'text-muted-foreground/80 hover:bg-accent/40 hover:text-foreground'
          }`}
        >
          <div
            className={`p-1.5 rounded-lg transition-colors ${isActiveConversations ? 'bg-primary/20' : 'bg-transparent'}`}
          >
            <Activity className="size-4 shrink-0" />
          </div>
          <span className="flex-1">Активные встречи</span>
          {activeTotalCount > 0 && (
            <span className="inline-flex items-center justify-center size-5 rounded-full text-[10px] font-bold bg-blue-500/20 text-blue-500 border border-blue-500/30">
              {activeTotalCount}
            </span>
          )}
        </Link>

        {isOwnerOrAdmin && (
          <Link
            to={`${routes.COMPANY_MANAGEMENT}/${currentCompanyId}`}
            className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 active:scale-[0.98] ${
              isCompanyManagement
                ? 'bg-primary/10 text-primary shadow-[inset_0_0_0_1px_rgba(var(--primary),0.1)]'
                : 'text-muted-foreground/80 hover:bg-accent/40 hover:text-foreground'
            }`}
          >
            <div
              className={`p-1.5 rounded-lg transition-colors ${isCompanyManagement ? 'bg-primary/20' : 'bg-transparent'}`}
            >
              <Settings2 className="size-4 shrink-0" />
            </div>
            Компания
          </Link>
        )}

        {!isSelfHostedDeployment() && (
          <Link
            to={routes.TARIFFS}
            className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 active:scale-[0.98] ${
              isTariffs
                ? 'bg-primary/10 text-primary shadow-[inset_0_0_0_1px_rgba(var(--primary),0.1)]'
                : 'text-muted-foreground/80 hover:bg-accent/40 hover:text-foreground'
            }`}
          >
            <div
              className={`p-1.5 rounded-lg transition-colors ${isTariffs ? 'bg-primary/20' : 'bg-transparent'}`}
            >
              <CreditCard className="size-4 shrink-0" />
            </div>
            Тарифы
          </Link>
        )}

        {showSupportCenter && (
          <Link
            to={routes.SUPPORT}
            className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 active:scale-[0.98] ${
              isSupport
                ? 'bg-primary/10 text-primary shadow-[inset_0_0_0_1px_rgba(var(--primary),0.1)]'
                : 'text-muted-foreground/80 hover:bg-accent/40 hover:text-foreground'
            }`}
          >
            <div
              className={`p-1.5 rounded-lg transition-colors ${isSupport ? 'bg-primary/20' : 'bg-transparent'}`}
            >
              <LifeBuoy className="size-4 shrink-0" />
            </div>
            <span className="flex-1">Поддержка</span>
            {supportUnreadCount > 0 && (
              <span className="inline-flex min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-bold bg-amber-500/20 text-amber-600 border border-amber-500/30">
                {supportUnreadCount > 99 ? '99+' : supportUnreadCount}
              </span>
            )}
          </Link>
        )}

        <Link
          to={routes.PROFILE}
          className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 active:scale-[0.98] ${
            isProfile
              ? 'bg-primary/10 text-primary shadow-[inset_0_0_0_1px_rgba(var(--primary),0.1)]'
              : 'text-muted-foreground/80 hover:bg-accent/40 hover:text-foreground'
          }`}
        >
          <div
            className={`p-1.5 rounded-lg transition-colors ${isProfile ? 'bg-primary/20' : 'bg-transparent'}`}
          >
            <User className="size-4 shrink-0" />
          </div>
          Профиль
        </Link>
      </nav>

      <div className="px-3 py-3 mt-auto border-t border-border/20 space-y-1">
        {authMe?.email && (
          <div className="mb-2 rounded-xl border border-border/40 bg-background/40 px-3 py-3">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-primary/10 p-1.5 text-primary">
                <User className="size-4 shrink-0" />
              </div>
              <div className="min-w-0 space-y-0.5">
                {authMe.display_name && (
                  <p className="truncate text-sm font-semibold text-foreground">
                    {authMe.display_name}
                  </p>
                )}
                <p
                  className={`truncate ${
                    authMe.display_name
                      ? 'text-xs text-muted-foreground/80'
                      : 'text-sm font-medium text-foreground'
                  }`}
                >
                  {authMe.email}
                </p>
              </div>
            </div>
          </div>
        )}
        <ThemeToggler setTheme={setTheme} theme={theme} resolvedTheme={theme as Resolved}>
          {({ effective, toggleTheme }) => {
            const nextTheme = effective === 'dark' ? 'light' : 'dark';
            return (
              <button
                type="button"
                className="group flex items-center gap-3 w-full rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground/80 hover:bg-accent/40 hover:text-foreground transition-all duration-200 cursor-pointer active:scale-[0.98]"
                onClick={() => toggleTheme(nextTheme)}
              >
                <div className="p-1.5 rounded-lg bg-transparent transition-colors group-hover:bg-accent/30">
                  {effective === 'dark' ? (
                    <Moon className="size-4 shrink-0" />
                  ) : (
                    <Sun className="size-4 shrink-0" />
                  )}
                </div>
                <span>{effective === 'dark' ? 'Тёмная тема' : 'Светлая тема'}</span>
              </button>
            );
          }}
        </ThemeToggler>
        {showLogoutButton && (
          <button
            type="button"
            className="group flex items-center gap-3 w-full rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground/80 hover:bg-red-500/10 hover:text-red-500 dark:hover:bg-red-500/15 dark:hover:text-red-400 transition-all duration-200 cursor-pointer active:scale-[0.98]"
            onClick={() => handleLogout()}
          >
            <div className="p-1.5 rounded-lg bg-transparent transition-colors group-hover:bg-red-500/10">
              <LogOut className="size-4 shrink-0" />
            </div>
            <span>Выход из аккаунта</span>
          </button>
        )}
      </div>
    </aside>
  );
};
